import { useState, useEffect, useMemo, useRef } from "react"
import {
  Package, PackagePlus, Boxes, ShoppingCart, BarChart3, ChevronLeft,
  ChevronRight, ChevronDown, Minus, Plus, Pencil, Trash2, Lock, Home,
  AlertTriangle, CircleCheck, ClipboardList, Store, Copy,
} from "lucide-react"
import { DS } from "../lib/ui"
import CarteTitrePage from "../components/CarteTitrePage"
import { isMoisClos } from "../lib/depots-helpers"
import { splitConsommables } from "../lib/consommables-helpers"  // ADR-210
// Ce qui manque, vu par un rôle terrain : phrase de contrôle + tri par manque
// RELATIF. Aucun prix ne transite par ces helpers.
import { resumeStock, sousSeuil as estSousSeuil } from "../lib/stock-alerte"
import { getAllLogsFlat } from "../lib/sousloc-helpers"
import SaisirDepotModal from "../modals/SaisirDepotModal"
import StockEcran from "./StockEcran"
import { supabase } from "../supabase"
import SaisirAchatModal from "../modals/SaisirAchatModal"

// DA (DESIGN.md §8 — Niveau 1 board/listes/cartes) : habillage seulement.
// Tokens marine/ciel/corail + sémantique (niveau bas = warning/danger) ;
// emojis → icônes lucide ; cartes `.fbz-card`. AUCUNE logique/donnée touchée :
// les compteurs de stock (_bumpStock côté serveur), le calcul de coût et la
// persistance restent strictement identiques — habillage présentationnel pur.

// Stepper qty stock central (admin) — état local pour saisie fluide :
// les boutons ± committent immédiatement ; le champ libre committe sur blur/Enter
// (évite un POST /api/stock par frappe et les conflits de curseur avec le snapshot
// serveur ré-appliqué). Se resynchronise quand la valeur serveur change.
function QtyStepper({ qty, onCommit }) {
  const [local, setLocal] = useState(String(qty))
  useEffect(() => { setLocal(String(qty)) }, [qty])
  const commit = (v) => { const n = Math.max(0, parseInt(v, 10) || 0); if (n !== qty) onCommit(n) }
  return (
    <div style={{display:"flex",alignItems:"center",gap:6}}>
      <button className="fbz-press" aria-label="Diminuer la quantité" onClick={()=>onCommit(Math.max(0, qty-1))} disabled={qty<=0}
        style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:30,height:30,borderRadius:8,background:qty>0?DS.cielSoft:DS.pageBg,color:qty>0?DS.marine:DS.borderHover,border:`0.5px solid ${DS.border}`,cursor:qty>0?"pointer":"not-allowed"}}><Minus size={15} strokeWidth={2.4}/></button>
      <input type="number" min="0" value={local}
        onChange={e=>setLocal(e.target.value)}
        onBlur={e=>commit(e.target.value)}
        onKeyDown={e=>{ if (e.key === "Enter") e.target.blur() }}
        style={{width:60,textAlign:"center",border:`0.5px solid ${DS.borderHover}`,borderRadius:8,padding:"5px 6px",fontSize:14,fontWeight:700,fontFamily:DS.fontDisplay,outline:"none",background:DS.pageBg,color:DS.marine}}/>
      <button className="fbz-press" aria-label="Augmenter la quantité" onClick={()=>onCommit(qty+1)}
        style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:30,height:30,borderRadius:8,background:DS.marine,color:"#fff",border:"none",cursor:"pointer"}}><Plus size={15} strokeWidth={2.4}/></button>
    </div>
  )
}

// Onglet Stock — refonte 2026-04-29 (modèle dépôts/achats, unité prélevable).
// Sous-onglets :
//   Dépôts        : saisie + liste du mois (admin & ménage)
//   Stock central : qtyDispo (prélevable) + seuilMin par produit, achats récents
//   Courses       : produits sous seuil, calcul nbPaquets + récap par enseigne
//   Récap mensuel : par logement et par produit (admin)
export default function StockTab({
  masquerEcran = false,
  cfg, depots, achats, stockCentral,
  stockApi, applyStockSnapshot,
  fmtE, formatMois, fmtJourMois, month, navM, isAdmin, currentUser,
}) {
  const [activeSubTab, setActiveSubTab] = useState("depots")
  // L'ÉCRAN STOCK (maquette 2026-08-21) : l'orchestrateur ne voit QUE lui ;
  // l'admin l'a en tête d'onglet, ses outils (dépôts/achats/courses/récap)
  // restent dessous, repliés.
  const estOrchestrateur = currentUser?.role === "orchestrateur"
  const [outilsOuverts, setOutilsOuverts] = useState(false)
  // Coûts par ménage (ADMIN seul) : nb de ménages FAITS du mois, par logement.
  const [runsMois, setRunsMois] = useState({})
  useEffect(() => {
    if (!isAdmin || !month) return
    let annule = false
    ;(async () => {
      try {
        const fin = (() => { const [y, m] = month.split("-").map(Number)
          return m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, "0")}-01` })()
        const { data } = await supabase.from("menage_runs")
          .select("logement_id").eq("status", "completed")
          .gte("scheduled_date", `${month}-01`).lt("scheduled_date", fin).limit(2000)
        if (annule) return
        const par = {}
        for (const r of data || []) par[r.logement_id] = (par[r.logement_id] || 0) + 1
        setRunsMois(par)
      } catch (_) { /* best-effort */ }
    })()
    return () => { annule = true }
  }, [isAdmin, month])
  const [depotModal, setDepotModal] = useState(null) // null | {editing?, defaultLogId?}
  const [achatModal, setAchatModal] = useState(null)
  const [confirmDel, setConfirmDel] = useState(null)
  // Filtres sous-onglet Dépôts (admin)
  const [filterLog, setFilterLog] = useState("")
  const [filterAuteur, setFilterAuteur] = useState("")
  const [filterCs, setFilterCs] = useState("")
  // Vue "Distribué ce mois" : { csId: bool }
  const [expandedDistrib, setExpandedDistrib] = useState({})
  // Toast : succès (post-save dépôt, avec Undo) OU erreur serveur (remontée).
  const [toast, setToast] = useState(null) // { type:'success', depotId } | { type:'error', message }
  const toastTimerRef = useRef(null)

  const tabs = useMemo(() => isAdmin
    ? [["depots","Dépôts",PackagePlus],["stock-central","Stock central",Boxes],["courses","Courses",ShoppingCart],["recap","Récap",BarChart3]]
    : [["depots","Dépôts",PackagePlus],["stock-central","Stock central",Boxes]]
  , [isAdmin])

  useEffect(() => {
    if (!tabs.find(([id]) => id === activeSubTab)) setActiveSubTab("depots")
  }, [tabs, activeSubTab])

  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current) }, [])

  const allLogs = useMemo(() => getAllLogsFlat(cfg), [cfg])
  // L'orchestrateur est cloisonné par logIds. logIds = [] → AUCUN logement
  // accessible (décision arbitrée PR admin-comptes).
  // Côté admin : pas de restriction (userLogIds=null sentinel).
  // L'agent de ménage n'arrive jamais ici : aucun accès au stock (décision 53).
  const userLogIds = useMemo(
    () => estOrchestrateur ? (currentUser.logIds || []) : null,
    [currentUser, estOrchestrateur]
  )
  const accessibleLogs = useMemo(
    () => userLogIds === null ? allLogs : allLogs.filter(l => userLogIds.includes(l.id)),
    [allLogs, userLogIds]
  )
  const canCreateDepot = accessibleLogs.length > 0

  const { kitProduits, operProduits } = splitConsommables(cfg)  // ADR-210 — helper partagé
  const allProduits  = [...kitProduits, ...operProduits]

  // Orchestrateur : ne lister que les dépôts de ses logements.
  // `allLogs` reste utilisé pour les helpers d'affichage (logName, isLogSousLoc)
  // car ils doivent résoudre n'importe quel logId pour rendre les libellés.
  const depotsThisMonth = (depots || [])
    .filter(d => d.mois === month)
    .filter(d => userLogIds === null || userLogIds.includes(d.logId))
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
  const achatsThisMonth = (achats || []).filter(a => (a.date || "").startsWith(month)).sort((a, b) => (b.date || "").localeCompare(a.date || ""))

  const logName = (logId) => {
    const log = allLogs.find(l => l.id === logId)
    if (!log) return logId
    return log.nom
  }
  const isLogSousLoc = (logId) => allLogs.find(l => l.id === logId)?.propId === "p6"
  const csName = (csId) => allProduits.find(p => p.id === csId)?.nom || csId

  // Édition / suppression d'un dépôt :
  //   - admin : tous les dépôts
  //   - orchestrateur : uniquement les saisies de terrain (auteur === "menage")
  // Le verrou "mois clos" (isMoisClos) reste appliqué via `disabled` séparément.
  const canActOnDepot = (d) =>
    isAdmin || (estOrchestrateur && d.auteur === "menage")

  // Libellé pour 1 unité prélevable d'un produit (singulier).
  // - qtyA == qtyP : formatAchat ("paquet" / "pack" / "carton") + " de " + qtyA
  // - qtyA > qtyP  : "sous-paquet de " + qtyP
  const labelPrelevable = (p) => {
    const qtyA = p?.qtyParAchat ?? 1
    const qtyP = p?.qtyParPrelevable ?? qtyA
    if (qtyA === qtyP) return `${p?.formatAchat || "paquet"} de ${qtyA}`
    return `sous-paquet de ${qtyP}`
  }

  // ── Toast ──
  const showToast = (depotId) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    setToast({ type: "success", depotId })
    toastTimerRef.current = setTimeout(() => setToast(null), 5000)
  }
  const showErrorToast = (message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    setToast({ type: "error", message })
    toastTimerRef.current = setTimeout(() => setToast(null), 7000)
  }
  const dismissToast = () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    setToast(null)
  }

  // ── Handlers ──
  // Lot 1 : toute mutation passe par l'endpoint /api/stock (authz role-aware +
  // cascade stock_central + dual-write KV côté serveur). Le snapshot renvoyé est
  // ré-appliqué via applyStockSnapshot. Toute erreur serveur (403 mois clos /
  // logement non accessible, 500…) est remontée en toast rouge ; la modale reste
  // ouverte pour permettre une nouvelle tentative.

  // Ajout / édition d'un dépôt. La cascade (delta lignes → qtyDispo) est calculée
  // serveur à partir du dépôt existant relu en base (le `previous` client est ignoré).
  const handleSaveDepot = async (depot) => {
    try {
      applyStockSnapshot(await stockApi.depotUpsert(depot))
      setDepotModal(null)
      showToast(depot.id)
    } catch (e) {
      console.error("[StockTab] depot_upsert", e)
      showErrorToast(e.message || "Échec de l'enregistrement du dépôt")
    }
  }

  const handleSaveAchat = async (achat) => {
    try {
      applyStockSnapshot(await stockApi.achatCreate(achat))
      setAchatModal(null)
    } catch (e) {
      console.error("[StockTab] achat_create", e)
      showErrorToast(e.message || "Échec de l'enregistrement de l'achat")
    }
  }

  const handleDeleteDepot = async (depotId) => {
    try {
      applyStockSnapshot(await stockApi.depotDelete(depotId))
      setConfirmDel(null)
    } catch (e) {
      console.error("[StockTab] depot_delete", e)
      showErrorToast(e.message || "Échec de la suppression du dépôt")
    }
  }

  const handleDeleteAchat = async (achatId) => {
    try {
      applyStockSnapshot(await stockApi.achatDelete(achatId))
      setConfirmDel(null)
    } catch (e) {
      console.error("[StockTab] achat_delete", e)
      showErrorToast(e.message || "Échec de la suppression de l'achat")
    }
  }

  const handleUndoToast = async () => {
    if (toast?.type !== "success" || !toast?.depotId) return
    await handleDeleteDepot(toast.depotId)
    dismissToast()
  }

  const handleUpdateQtyDispo = async (csId, qty) => {
    try {
      applyStockSnapshot(await stockApi.stockSet(csId, { qtyDispo: Math.max(0, Number(qty) || 0) }))
    } catch (e) {
      console.error("[StockTab] stock_set qty", e)
      showErrorToast(e.message || "Échec de la mise à jour du stock")
    }
  }

  const handleUpdateSeuilMin = async (csId, seuil) => {
    try {
      applyStockSnapshot(await stockApi.stockSet(csId, { seuilMin: Math.max(0, Number(seuil) || 0) }))
    } catch (e) {
      console.error("[StockTab] stock_set seuil", e)
      showErrorToast(e.message || "Échec de la mise à jour du seuil")
    }
  }

  // ── Filtres dépôts (admin) ──
  const depotsFiltered = useMemo(() => {
    if (!isAdmin) return depotsThisMonth
    return depotsThisMonth.filter(d => {
      if (filterLog && d.logId !== filterLog) return false
      if (filterAuteur && d.auteur !== filterAuteur) return false
      if (filterCs && !(d.lignes || []).some(l => l.csId === filterCs)) return false
      return true
    })
  }, [depotsThisMonth, filterLog, filterAuteur, filterCs, isAdmin])

  const filterLogOptions = useMemo(() =>
    [...new Set(depotsThisMonth.map(d => d.logId))].sort()
  , [depotsThisMonth])
  const filterCsOptions = useMemo(() =>
    [...new Set(depotsThisMonth.flatMap(d => (d.lignes || []).map(l => l.csId)))].sort()
  , [depotsThisMonth])
  const filterAuteurOptions = useMemo(() =>
    [...new Set(depotsThisMonth.map(d => d.auteur).filter(Boolean))]
  , [depotsThisMonth])

  // ── Distribué ce mois (par produit) ──
  const distribByCs = useMemo(() => {
    const m = {}
    depotsThisMonth.forEach(d => {
      ;(d.lignes || []).forEach(l => {
        if (!m[l.csId]) m[l.csId] = { total: 0, parLog: {} }
        m[l.csId].total += l.nbPrelevables || 0
        m[l.csId].parLog[d.logId] = (m[l.csId].parLog[d.logId] || 0) + (l.nbPrelevables || 0)
      })
    })
    return m
  }, [depotsThisMonth])

  // Styles partagés (DA)
  const selectStyle = {width:"100%",border:`0.5px solid ${DS.borderHover}`,borderRadius:7,padding:"6px 8px",fontSize:11.5,fontFamily:DS.fontBody,outline:"none",background:DS.card,color:DS.marine}
  const labelStyle = {display:"block",fontSize:9.5,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:3}
  const monthNavBtn = {display:"inline-flex",alignItems:"center",justifyContent:"center",width:34,height:34,background:DS.cielSoft,color:DS.marine,border:"none",borderRadius:8,cursor:"pointer"}
  const infoBox = {background:DS.cielSoft,borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:11.5,color:DS.marineDeep,lineHeight:1.5}

  // ── Render ──
  return (
    <div style={{fontFamily:DS.fontBody}}>
      {depotModal && (
        <SaisirDepotModal
          allLogs={accessibleLogs}
          kitProduits={kitProduits}
          operProduits={operProduits}
          stockCentral={stockCentral}
          editing={depotModal.editing}
          defaultLogId={depotModal.defaultLogId}
          defaultAuteur={isAdmin ? "admin" : "menage"}
          onSave={handleSaveDepot}
          onClose={()=>setDepotModal(null)}
        />
      )}
      {achatModal && (
        <SaisirAchatModal
          produits={allProduits}
          defaultCsId={achatModal.defaultCsId}
          onSave={handleSaveAchat}
          onClose={()=>setAchatModal(null)}
        />
      )}

      {confirmDel && (
        <div style={{position:"fixed",inset:0,background:"rgba(20,51,80,.45)",zIndex:1002,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:DS.card,borderRadius:16,padding:24,maxWidth:340,width:"100%",fontFamily:DS.fontBody}}>
            <div style={{fontFamily:DS.fontDisplay,fontWeight:600,color:DS.marineDark,fontSize:17,marginBottom:8}}>{confirmDel.title}</div>
            <div style={{fontSize:12.5,color:DS.textSecondary,marginBottom:16,lineHeight:1.5}}>{confirmDel.msg}</div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="fbz-press" onClick={()=>setConfirmDel(null)} style={{padding:"8px 14px",background:DS.card,color:DS.textSecondary,border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,fontWeight:600,fontSize:12.5,cursor:"pointer",fontFamily:DS.fontBody}}>Annuler</button>
              <button className="fbz-press" onClick={confirmDel.onConfirm} style={{padding:"8px 14px",background:DS.danger,color:"#fff",border:"none",borderRadius:DS.radiusControl,fontWeight:700,fontSize:12.5,cursor:"pointer",fontFamily:DS.fontBody}}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast : succès (avec Undo) ou erreur serveur (rouge, sans Undo) */}
      {toast && (
        <div role={toast.type === "error" ? "alert" : undefined}
          style={{position:"fixed",bottom:24,right:24,zIndex:1003,background:toast.type==="error"?DS.danger:DS.marine,color:"#fff",padding:"10px 14px",borderRadius:10,boxShadow:"0 8px 24px rgba(20,51,80,.28)",display:"flex",alignItems:"center",gap:12,fontFamily:DS.fontBody,fontSize:12.5,fontWeight:600,maxWidth:340}}>
          {toast.type === "error" ? (
            <span style={{display:"inline-flex",alignItems:"center",gap:7}}><AlertTriangle size={15} strokeWidth={2.2}/> {toast.message}</span>
          ) : (
            <>
              <span style={{display:"inline-flex",alignItems:"center",gap:7}}><CircleCheck size={15} strokeWidth={2.2}/> Dépôt enregistré</span>
              <button className="fbz-press" onClick={handleUndoToast}
                style={{background:"rgba(255,255,255,.18)",color:"#fff",border:"none",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:DS.fontBody}}>
                Annuler
              </button>
            </>
          )}
          <button className="fbz-press" onClick={dismissToast}
            style={{background:"none",color:"rgba(255,255,255,.65)",border:"none",fontSize:18,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
      )}

      {/* Carte de titre (lot apparence 2026-08-14). */}
      {/* L'ÉCRAN STOCK (maquette 2026-08-21) — orchestrateur : c'est TOUT
          l'onglet ; admin : en tête, ses outils restent dessous, repliés. */}
      {/* LOT 3 (2026-08-26) : chez l'admin, l'écran Stock d'Aujourd'hui
          (JourStockCtrl, proto) REMPLACE cette maquette — `masquerEcran` la
          tait, les outils historiques restent dessous, repliés. */}
      {(estOrchestrateur || (isAdmin && !masquerEcran)) && (
        <StockEcran cfg={cfg} currentUser={currentUser} isAdmin={isAdmin}
          fmtE={fmtE} depotsMois={depots} runsMoisParLogement={runsMois}
          onDeposer={(logId) => setDepotModal({ defaultLogId: logId })} />
      )}
      {estOrchestrateur ? null : (
        <>
      {isAdmin ? (
        <button type="button" data-testid="stock-outils-toggle"
          onClick={() => setOutilsOuverts((v) => !v)} aria-expanded={outilsOuverts}
          className="fbz-press"
          style={{ width: "100%", display: "flex", alignItems: "center", gap: 8,
            background: "#f4f9fd", border: "none", borderRadius: 11,
            padding: "10px 12px", margin: "6px 0 12px", cursor: "pointer",
            textAlign: "left", fontFamily: DS.fontBody }}>
          <Boxes size={15} strokeWidth={2.2} color="#5c6773" style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 11.5, color: "#233240", fontWeight: 600 }}>
            Outils du stock — dépôts, achats, courses, récap mensuel
          </span>
          {outilsOuverts
            ? <ChevronDown size={13} strokeWidth={2.4} color="#c3ccd4" style={{ flexShrink: 0 }} />
            : <ChevronRight size={13} strokeWidth={2.4} color="#c3ccd4" style={{ flexShrink: 0 }} />}
        </button>
      ) : (
        <CarteTitrePage titre="Stock" />
      )}
      {(!isAdmin || outilsOuverts) && (
        <>
      <div style={{display:"flex",background:DS.card,borderRadius:DS.radiusCard,padding:4,marginBottom:14,gap:4,border:`0.5px solid ${DS.border}`,flexWrap:"wrap"}}>
        {tabs.map(([id, label, Icon]) => {
          const active = activeSubTab === id
          return (
            <button key={id} className="fbz-press" onClick={()=>setActiveSubTab(id)}
              style={{flex:1,minWidth:90,display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,padding:"9px 6px",background:active?DS.marine:"transparent",border:"none",borderRadius:DS.radiusControl,fontWeight:600,fontSize:11.5,color:active?"#fff":DS.textSecondary,cursor:"pointer",fontFamily:DS.fontBody,transition:`background ${DS.durBase}`}}>
              <Icon size={15} strokeWidth={2}/> {label}
            </button>
          )
        })}
      </div>

      {/* ── Sous-onglet : DÉPÔTS ── */}
      {activeSubTab === "depots" && (
        <>
          <div className="fbz-card" style={{background:DS.card,borderRadius:DS.radiusCard,padding:"8px 12px",marginBottom:14,border:`0.5px solid ${DS.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <button className="fbz-press fbz-tap" onClick={()=>navM(-1)} style={monthNavBtn}><ChevronLeft size={18} strokeWidth={2.4}/></button>
            <div style={{fontFamily:DS.fontDisplay,fontSize:16,fontWeight:600,color:DS.marineDark}}>{formatMois(month)}</div>
            <button className="fbz-press fbz-tap" onClick={()=>navM(1)} style={monthNavBtn}><ChevronRight size={18} strokeWidth={2.4}/></button>
          </div>

          <button className="fbz-press fbz-tap" onClick={()=>canCreateDepot && setDepotModal({})} disabled={!canCreateDepot}
            title={canCreateDepot ? "" : "Aucun logement accessible — contacter l'admin"}
            style={{width:"100%",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:8,padding:"12px 18px",background:canCreateDepot?DS.corail:DS.borderHover,color:"#fff",border:"none",borderRadius:DS.radiusControl,fontWeight:600,fontSize:13,cursor:canCreateDepot?"pointer":"not-allowed",fontFamily:DS.fontBody,marginBottom:14,opacity:canCreateDepot?1:0.7}}>
            <PackagePlus size={17} strokeWidth={2.2}/> Saisir un dépôt
          </button>

          {/* Filtres admin */}
          {isAdmin && depotsThisMonth.length > 0 && (
            <div style={{background:DS.card,borderRadius:DS.radiusCard,padding:"10px 14px",marginBottom:10,border:`0.5px solid ${DS.border}`,display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(min(100%, 150px), 1fr))",gap:8}}>
              <div>
                <label style={labelStyle}>Logement</label>
                <select value={filterLog} onChange={e=>setFilterLog(e.target.value)} style={selectStyle}>
                  <option value="">Tous</option>
                  {filterLogOptions.map(lid => <option key={lid} value={lid}>{logName(lid)}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Auteur</label>
                <select value={filterAuteur} onChange={e=>setFilterAuteur(e.target.value)} style={selectStyle}>
                  <option value="">Tous</option>
                  {filterAuteurOptions.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Produit</label>
                <select value={filterCs} onChange={e=>setFilterCs(e.target.value)} style={selectStyle}>
                  <option value="">Tous</option>
                  {filterCsOptions.map(cid => <option key={cid} value={cid}>{csName(cid)}</option>)}
                </select>
              </div>
            </div>
          )}

          {depotsFiltered.length === 0 ? (
            <div style={{textAlign:"center",padding:"34px 20px",background:DS.card,borderRadius:DS.radiusCard,border:`0.5px solid ${DS.border}`}}>
              <div style={{width:52,height:52,borderRadius:15,background:DS.cielSoft,display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:10}}><PackagePlus size={24} strokeWidth={1.8} color={DS.marine}/></div>
              <div style={{fontSize:13,color:DS.textSecondary}}>
                {depotsThisMonth.length === 0 ? `Aucun dépôt pour ${formatMois(month)}` : "Aucun dépôt ne correspond aux filtres"}
              </div>
            </div>
          ) : depotsFiltered.map(d => {
            const sousLoc = isLogSousLoc(d.logId)
            const closed = isMoisClos(d.mois)
            return (
              <div key={d.id} className="fbz-card" style={{background:DS.card,borderRadius:DS.radiusCard,padding:14,marginBottom:8,border:`0.5px solid ${DS.border}`,borderLeft:`3px solid ${sousLoc ? DS.ciel : DS.marine}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:6}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                      <span style={{display:"inline-flex",alignItems:"center",gap:5,fontWeight:700,fontSize:13,color:DS.marineDark}}><Home size={13} strokeWidth={2} color={DS.marine}/> {logName(d.logId)}</span>
                      {sousLoc && <span style={{display:"inline-flex",alignItems:"center",gap:4,background:DS.cielSoft,color:DS.marine,fontSize:9.5,fontWeight:700,padding:"2px 7px",borderRadius:6,letterSpacing:.3}}><Home size={10} strokeWidth={2.4}/> SOUS-LOC</span>}
                      {closed && <span title="Mois clos" style={{display:"inline-flex",alignItems:"center",gap:4,background:DS.pageBg,color:DS.textMuted,fontSize:9.5,fontWeight:700,padding:"2px 7px",borderRadius:6}}><Lock size={10} strokeWidth={2.4}/> Mois clos</span>}
                    </div>
                    <div style={{fontSize:11,color:DS.textSecondary,marginTop:3}}>
                      {fmtJourMois(d.date)} · {d.lignes.length} produit{d.lignes.length>1?"s":""}
                      {d.auteur && <span style={{color:DS.textMuted}}> · {d.auteur}{d.initiales ? ` (${d.initiales})` : ""}</span>}
                    </div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    {isAdmin && <div style={{fontSize:14,fontWeight:700,fontFamily:DS.fontDisplay,color:DS.charge}}>{fmtE(d.coutTotal)}</div>}
                    {canActOnDepot(d) && (
                      <div style={{display:"flex",gap:4,marginTop:4,justifyContent:"flex-end"}}>
                        <button className={closed ? undefined : "fbz-press fbz-tap"} onClick={()=>!closed && setDepotModal({ editing: d })}
                          disabled={closed}
                          title={closed ? "Mois clos, modification désactivée" : "Modifier"}
                          style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:28,height:28,background:closed?DS.pageBg:DS.cielSoft,color:closed?DS.borderHover:DS.marine,border:"none",borderRadius:7,cursor:closed?"not-allowed":"pointer"}}>
                          <Pencil size={14} strokeWidth={2}/>
                        </button>
                        <button className={closed ? undefined : "fbz-press fbz-tap"} onClick={()=>!closed && setConfirmDel({
                            title: "Supprimer ce dépôt ?",
                            msg: `${logName(d.logId)} · ${fmtJourMois(d.date)} · ${fmtE(d.coutTotal)}. Le stock central sera re-crédité.`,
                            onConfirm: () => handleDeleteDepot(d.id),
                          })}
                          disabled={closed}
                          title={closed ? "Mois clos, suppression désactivée" : "Supprimer"}
                          style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:28,height:28,background:closed?DS.pageBg:DS.dangerBg,color:closed?DS.borderHover:DS.danger,border:"none",borderRadius:7,cursor:closed?"not-allowed":"pointer"}}>
                          <Trash2 size={14} strokeWidth={2}/>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{fontSize:11.5,color:DS.textSecondary,lineHeight:1.6}}>
                  {d.lignes.map(l => {
                    const prod = allProduits.find(p => p.id === l.csId)
                    const lab = prod ? labelPrelevable(prod) : "u"
                    return (
                      <span key={l.csId} style={{display:"inline-block",marginRight:10}}>
                        <span style={{fontWeight:700,color:DS.marine}}>{l.nbPrelevables}</span> {csName(l.csId)} <span style={{color:DS.textMuted}}>({lab})</span>
                      </span>
                    )
                  })}
                </div>
                {d.notes && <div style={{fontSize:11,color:DS.textMuted,fontStyle:"italic",marginTop:6}}>{d.notes}</div>}
              </div>
            )
          })}
        </>
      )}

      {/* ── Sous-onglet : STOCK CENTRAL ── */}
      {/* PHRASE DE CONTRÔLE, rôle terrain uniquement. L'admin a déjà l'onglet
          Courses, qui dit la même chose en plus détaillé. L'orchestrateur, lui,
          n'achète pas : il a besoin de savoir quoi SIGNALER, et il voyait
          jusqu'ici un chiffre rouge sans libellé. */}
      {activeSubTab === "stock-central" && !isAdmin && (() => {
        // ⚠️ Les quantités vivent dans `stockCentral`, pas dans le catalogue :
        // `allProduits` ne porte que le nom, le format et le prix. Calculer la
        // phrase sur le catalogue seul l'aurait rendue toujours rassurante.
        const avecQuantites = allProduits.map((prod) => ({
          ...prod,
          qtyDispo: stockCentral[prod.id]?.qtyDispo || 0,
          seuilMin: stockCentral[prod.id]?.seuilMin || 0,
        }))
        const r = resumeStock(avecQuantites)
        return (
          <div data-testid="stock-resume"
            style={{fontSize:12.5, marginBottom:12,
              color: r.alerte ? DS.dangerText : DS.textSecondary,
              fontWeight: r.alerte ? 600 : 400}}>
            {r.texte}
          </div>
        )
      })()}

      {activeSubTab === "stock-central" && (
        <>
          <div style={infoBox}>
            Stock central FBZ en unités prélevables (= unités physiques manipulées par le ménage).
          </div>

          {isAdmin && (
            <button className="fbz-press fbz-tap" onClick={()=>setAchatModal({})}
              style={{width:"100%",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:8,padding:"12px 18px",background:DS.corail,color:"#fff",border:"none",borderRadius:DS.radiusControl,fontWeight:600,fontSize:13,cursor:"pointer",fontFamily:DS.fontBody,marginBottom:14}}>
              <ShoppingCart size={16} strokeWidth={2.2}/> Saisir un achat
            </button>
          )}

          {allProduits.map(p => {
            const entry = stockCentral[p.id] || { qtyDispo: 0, seuilMin: 0 }
            const qty = entry.qtyDispo || 0
            const seuilMin = entry.seuilMin || 0
            // Aligné sur le helper testé (`<=`, pas `<`) : à quantité ÉGALE
            // au seuil on est déjà au point de commande. Deux définitions du
            // même mot dans le même écran finissent par se contredire — la
            // phrase de contrôle aurait annoncé un produit que le badge ne
            // signalait pas.
            const sousSeuil = estSousSeuil({ qtyDispo: qty, seuilMin })
            const lab = labelPrelevable(p)
            const distrib = distribByCs[p.id]
            const expanded = !!expandedDistrib[p.id]
            return (
              <div key={p.id} className="fbz-card" style={{background:DS.card,borderRadius:DS.radiusCard,padding:14,marginBottom:8,border:`0.5px solid ${DS.border}`}}>
                {isAdmin ? (
                  <>
                    <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                      <div style={{flex:1,minWidth:140}}>
                        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",fontWeight:700,fontSize:13,color:DS.marineDark}}>
                          <span>{p.nom} — <span style={{color:sousSeuil?DS.danger:DS.marine}}>{qty} {lab}{qty>1?"s":""}</span></span>
                          {sousSeuil && <span style={{display:"inline-flex",alignItems:"center",gap:4,background:DS.warningBg,color:DS.warningText,fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:6}}><AlertTriangle size={11} strokeWidth={2.4}/> Sous seuil</span>}
                        </div>
                        <div style={{fontSize:10.5,color:DS.textMuted,marginTop:2}}>{p.prixAchat?.toFixed(2)}€ / {p.formatAchat||"paquet"} · {p.enseigne||"—"}</div>
                      </div>
                      <QtyStepper qty={qty} onCommit={(v)=>handleUpdateQtyDispo(p.id, v)} />
                      <div style={{display:"flex",alignItems:"center",gap:4}}>
                        <span style={{fontSize:10,color:DS.textMuted,fontWeight:600}}>min:</span>
                        <input type="number" min="0" defaultValue={seuilMin}
                          onBlur={e=>{ const v = parseInt(e.target.value)||0; if (v !== seuilMin) handleUpdateSeuilMin(p.id, v) }}
                          onKeyDown={e=>{ if (e.key === "Enter") e.target.blur() }}
                          style={{width:60,textAlign:"center",border:`0.5px solid ${DS.warning}`,borderRadius:7,padding:"4px 6px",fontSize:12,fontWeight:700,fontFamily:DS.fontDisplay,outline:"none",background:DS.warningBg,color:DS.warningText}}/>
                      </div>
                    </div>

                    {/* Distribué ce mois (admin uniquement) */}
                    {distrib && (
                      <div style={{marginTop:8,paddingTop:8,borderTop:`0.5px solid ${DS.border}`}}>
                        <button className="fbz-press" onClick={()=>setExpandedDistrib(s=>({...s,[p.id]:!s[p.id]}))}
                          style={{background:"none",border:"none",cursor:"pointer",fontSize:11.5,color:DS.marine,fontWeight:600,padding:0,fontFamily:DS.fontBody,display:"flex",alignItems:"center",gap:5}}>
                          <BarChart3 size={14} strokeWidth={2}/> Distribué ce mois : {distrib.total} {lab}{distrib.total>1?"s":""}
                          <ChevronDown size={13} strokeWidth={2.4} color={DS.textMuted} style={{transform:expanded?"rotate(0deg)":"rotate(-90deg)",transition:`transform ${DS.durBase}`}}/>
                        </button>
                        {expanded && (
                          <div style={{marginTop:6,paddingLeft:12}}>
                            {Object.entries(distrib.parLog)
                              .sort((a,b) => b[1] - a[1])
                              .map(([logId, n]) => (
                                <div key={logId} style={{display:"flex",alignItems:"center",gap:5,fontSize:11.5,color:DS.textSecondary,padding:"2px 0"}}>
                                  <ChevronRight size={12} strokeWidth={2.2} color={DS.textMuted}/> {logName(logId)} : <span style={{fontWeight:700,color:DS.marine}}>{n}</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:13,color:DS.marineDark}}>{p.nom}</div>
                      <div style={{fontSize:10.5,color:DS.textMuted,marginTop:2}}>{lab}</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:20,fontWeight:600,color:sousSeuil?DS.danger:DS.marine,fontFamily:DS.fontDisplay}}>{qty}</div>
                      {/* Le badge n'existait que dans la branche admin : le rôle
                          terrain voyait un chiffre ROUGE sans savoir pourquoi.
                          Une couleur qui alerte sans dire de quoi est une
                          devinette, pas une information. */}
                      {sousSeuil
                        ? (
                          <div data-testid={`sous-seuil-${p.id}`}
                            style={{display:"inline-flex",alignItems:"center",gap:3,marginTop:2,
                              background:DS.warningBg,color:DS.warningText,fontSize:9.5,
                              fontWeight:700,padding:"2px 6px",borderRadius:6,whiteSpace:"nowrap"}}>
                            <AlertTriangle size={10} strokeWidth={2.4}/> Sous seuil
                          </div>
                        )
                        : <div style={{fontSize:9,color:DS.textMuted,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>dispo</div>}
                    </div>
                  </div>
                )}
              </div>
            )
          })}

          {isAdmin && achatsThisMonth.length > 0 && (
            <>
              <div style={{fontSize:11,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginTop:18,marginBottom:8}}>Achats du mois ({achatsThisMonth.length})</div>
              {achatsThisMonth.map(a => {
                const prod = allProduits.find(p => p.id === a.csId)
                const labMode = a.mode === "P" ? "sous-paquet" : "paquet"
                const labP = prod ? labelPrelevable(prod) : "u"
                const closed = isMoisClos((a.date || "").slice(0,7))
                return (
                  <div key={a.id} className="fbz-card" style={{background:DS.card,borderRadius:DS.radiusCard,padding:"10px 14px",marginBottom:6,border:`0.5px solid ${DS.border}`,display:"flex",alignItems:"center",gap:10}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                        <div style={{fontSize:12,fontWeight:600,color:DS.marineDark}}>{csName(a.csId)} <span style={{color:DS.textMuted,fontWeight:500}}>· {a.qtyAchetee} {labMode}{a.qtyAchetee>1?"s":""} (+{a.qtyAjoutPrelevable || 0} {labP})</span></div>
                        {closed && <span title="Mois clos" style={{display:"inline-flex",alignItems:"center",background:DS.pageBg,color:DS.textMuted,fontSize:9.5,fontWeight:700,padding:"2px 6px",borderRadius:6}}><Lock size={10} strokeWidth={2.4}/></span>}
                      </div>
                      <div style={{fontSize:10.5,color:DS.textMuted,marginTop:2}}>{fmtJourMois(a.date)}{a.enseigne ? ` · ${a.enseigne}` : ""}</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:13,fontWeight:700,fontFamily:DS.fontDisplay,color:DS.charge}}>{fmtE(a.prixTotal)}</div>
                      <div style={{fontSize:9,color:DS.textMuted}}>{a.prixUnitaire?.toFixed(3)} €/{labP}</div>
                    </div>
                    <button className={closed ? undefined : "fbz-press fbz-tap"} onClick={()=>!closed && setConfirmDel({
                        title: "Supprimer cet achat ?",
                        msg: `${csName(a.csId)} · ${a.qtyAchetee} ${labMode} · ${fmtE(a.prixTotal)}. Le stock central sera re-débité de ${a.qtyAjoutPrelevable || 0} ${labP}.`,
                        onConfirm: () => handleDeleteAchat(a.id),
                      })}
                      disabled={closed}
                      title={closed ? "Mois clos, suppression désactivée" : "Supprimer"}
                      style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:28,height:28,background:closed?DS.pageBg:DS.dangerBg,color:closed?DS.borderHover:DS.danger,border:"none",borderRadius:7,cursor:closed?"not-allowed":"pointer"}}>
                      <Trash2 size={14} strokeWidth={2}/>
                    </button>
                  </div>
                )
              })}
            </>
          )}
        </>
      )}

      {/* ── Sous-onglet : COURSES ── */}
      {activeSubTab === "courses" && isAdmin && (() => {
        // Liste de courses = produits dont qtyDispo < seuilMin.
        // Calcul nbPaquets = ceil((seuilMin - qtyDispo) / (qtyParAchat / qtyParPrelevable)).
        // Comme qtyDispo est en prélevable, le manque est en prélevable. 1 paquet acheté
        // contient (qtyParAchat / qtyParPrelevable) prélevables.
        const lignes = allProduits.map(p => {
          const entry = stockCentral[p.id] || { qtyDispo: 0, seuilMin: 0 }
          const qty = entry.qtyDispo || 0
          const seuil = entry.seuilMin || 0
          if (seuil <= 0 || qty >= seuil) return null
          const manque = seuil - qty
          const qtyA = p.qtyParAchat ?? 1
          const qtyP = p.qtyParPrelevable ?? qtyA
          const prelevablesParPaquet = qtyA / qtyP || 1
          const nbPaquets = Math.ceil(manque / prelevablesParPaquet)
          const cout = nbPaquets * (p.prixAchat || 0)
          return { p, qty, seuil, manque, nbPaquets, cout }
        }).filter(Boolean).sort((a, b) => b.cout - a.cout)

        const totalGlobal = lignes.reduce((s, l) => s + l.cout, 0)

        const parEnseigne = {}
        lignes.forEach(l => {
          const e = l.p.enseigne || "Autre"
          if (!parEnseigne[e]) parEnseigne[e] = { lignes: [], total: 0 }
          parEnseigne[e].lignes.push(l)
          parEnseigne[e].total += l.cout
        })
        const enseignes = Object.keys(parEnseigne).sort()

        const copierTexte = (enseigne) => {
          const ent = parEnseigne[enseigne]
          if (!ent) return
          const txtLignes = ent.lignes.map(l => `- ${l.nbPaquets} ${l.p.formatAchat || "paquet"}${l.nbPaquets>1?"s":""} ${l.p.nom} (${l.p.prixAchat?.toFixed(2)}€ × ${l.nbPaquets} = ${l.cout.toFixed(2)}€)`).join("\n")
          const txt = `${enseigne} :\n${txtLignes}\nTOTAL : ${ent.total.toFixed(2)}€`
          if (navigator.clipboard) {
            navigator.clipboard.writeText(txt).then(() => console.log("[StockTab] copié:", enseigne, ent.total + "€"))
          }
        }

        return (
          <>
            <div style={infoBox}>
              Liste de courses : produits dont le stock central est passé sous le seuil minimum.
            </div>

            {lignes.length === 0 ? (
              <div style={{textAlign:"center",padding:"34px 20px",background:DS.card,borderRadius:DS.radiusCard,border:`0.5px solid ${DS.border}`}}>
                <div style={{width:52,height:52,borderRadius:15,background:DS.successBg,display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:10}}><CircleCheck size={24} strokeWidth={1.8} color={DS.success}/></div>
                <div style={{fontSize:13,color:DS.textSecondary}}>Rien à acheter pour l'instant</div>
                <div style={{fontSize:11,color:DS.textMuted,marginTop:4}}>Tous les produits sont au-dessus de leur seuil minimum.</div>
              </div>
            ) : (
              <>
                <div className="fbz-card" style={{background:DS.card,borderRadius:DS.radiusCard,marginBottom:14,border:`0.5px solid ${DS.border}`,overflow:"hidden",borderLeft:`3px solid ${DS.warning}`}}>
                  <div style={{background:DS.warningBg,padding:"10px 14px"}}>
                    <div style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12,fontWeight:700,color:DS.warningText,letterSpacing:.3}}><ShoppingCart size={14} strokeWidth={2.2}/> PRODUITS SOUS SEUIL</div>
                    <div style={{fontSize:10.5,color:DS.warningText,opacity:.8,marginTop:2}}>{lignes.length} produit{lignes.length>1?"s":""} à acheter</div>
                  </div>
                  {lignes.map(l => {
                    const lab = labelPrelevable(l.p)
                    return (
                      <div key={l.p.id} className="fbz-rowhover" style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",borderBottom:`0.5px solid ${DS.border}`}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:600,color:DS.marineDark}}>{l.p.nom}</div>
                          <div style={{fontSize:10.5,color:DS.textMuted,marginTop:2}}>
                            Stock {l.qty}/{l.seuil} {lab} · manque {l.manque} · à acheter {l.nbPaquets} {l.p.formatAchat || "paquet"}{l.nbPaquets>1?"s":""}
                            {l.p.enseigne && <span> · {l.p.enseigne}</span>}
                          </div>
                        </div>
                        <div style={{textAlign:"right",flexShrink:0,marginLeft:10}}>
                          <div style={{fontSize:14,fontWeight:700,fontFamily:DS.fontDisplay,color:DS.charge}}>{l.cout.toFixed(2)}€</div>
                          <div style={{fontSize:9,color:DS.textMuted}}>{l.p.prixAchat?.toFixed(2)}€/{l.p.formatAchat || "paquet"}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {enseignes.length > 0 && (
                  <div className="fbz-card" style={{background:DS.card,borderRadius:DS.radiusCard,padding:14,marginBottom:14,border:`0.5px solid ${DS.border}`}}>
                    <div style={{display:"inline-flex",alignItems:"center",gap:7,fontFamily:DS.fontDisplay,fontSize:15,fontWeight:600,color:DS.marineDark,marginBottom:10}}><ClipboardList size={17} strokeWidth={2}/> Récap par enseigne</div>
                    {enseignes.map(e => (
                      <div key={e} style={{padding:"10px 0",borderBottom:`0.5px solid ${DS.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12,fontWeight:700,color:DS.marineDark}}><Store size={13} strokeWidth={2}/> {e}</div>
                          <div style={{fontSize:10.5,color:DS.textMuted,marginTop:2}}>{parEnseigne[e].lignes.length} ligne{parEnseigne[e].lignes.length>1?"s":""}</div>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <div style={{fontSize:14,fontWeight:700,fontFamily:DS.fontDisplay,color:DS.charge}}>{parEnseigne[e].total.toFixed(2)}€</div>
                          <button className="fbz-press fbz-tap" onClick={()=>copierTexte(e)} title="Copier la liste pour cette enseigne (SMS)"
                            style={{display:"inline-flex",alignItems:"center",gap:5,padding:"6px 11px",background:DS.cielSoft,color:DS.marine,border:"none",borderRadius:7,fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:DS.fontBody}}><Copy size={13} strokeWidth={2}/> Copier</button>
                        </div>
                      </div>
                    ))}
                    <div style={{padding:"12px 0 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span style={{fontSize:11,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5}}>Total estimé</span>
                      <span style={{fontFamily:DS.fontDisplay,fontSize:20,fontWeight:600,color:DS.marineDark}}>{totalGlobal.toFixed(2)}€</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )
      })()}

      {/* ── Sous-onglet : RÉCAP MENSUEL ── */}
      {activeSubTab === "recap" && isAdmin && (() => {
        // Par logement : { logId → { depots: [], coutTotal } }
        const parLog = {}
        depotsThisMonth.forEach(d => {
          if (!parLog[d.logId]) parLog[d.logId] = { depots: [], coutTotal: 0 }
          parLog[d.logId].depots.push(d)
          parLog[d.logId].coutTotal += Number(d.coutTotal) || 0
        })
        const logIdsSorted = Object.keys(parLog).sort((a, b) => parLog[b].coutTotal - parLog[a].coutTotal)

        // Par produit : { csId → { totalPrelevable, coutTotal, nbDepots } }
        const parProduit = {}
        depotsThisMonth.forEach(d => {
          ;(d.lignes || []).forEach(l => {
            if (!parProduit[l.csId]) parProduit[l.csId] = { totalPrelevable: 0, coutTotal: 0, nbDepots: 0 }
            parProduit[l.csId].totalPrelevable += Number(l.nbPrelevables) || 0
            parProduit[l.csId].coutTotal += (Number(l.nbPrelevables) || 0) * (Number(l.prixUnitaire) || 0)
            parProduit[l.csId].nbDepots += 1
          })
        })
        const csIdsSorted = Object.keys(parProduit).sort((a, b) => parProduit[b].coutTotal - parProduit[a].coutTotal)

        const totalMois = depotsThisMonth.reduce((s, d) => s + (Number(d.coutTotal) || 0), 0)

        return (
          <>
            <div className="fbz-card" style={{background:DS.card,borderRadius:DS.radiusCard,padding:"8px 12px",marginBottom:14,border:`0.5px solid ${DS.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <button className="fbz-press fbz-tap" onClick={()=>navM(-1)} style={monthNavBtn}><ChevronLeft size={18} strokeWidth={2.4}/></button>
              <div style={{fontFamily:DS.fontDisplay,fontSize:16,fontWeight:600,color:DS.marineDark}}>{formatMois(month)}</div>
              <button className="fbz-press fbz-tap" onClick={()=>navM(1)} style={monthNavBtn}><ChevronRight size={18} strokeWidth={2.4}/></button>
            </div>

            <div style={{background:DS.cielSoft,borderRadius:10,padding:"10px 14px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:11,fontWeight:700,color:DS.marineDeep,textTransform:"uppercase",letterSpacing:.5}}>Total dépôts {formatMois(month)}</span>
              <span style={{fontFamily:DS.fontDisplay,fontSize:20,fontWeight:600,color:DS.marineDark}}>{fmtE(totalMois)}</span>
            </div>

            {depotsThisMonth.length === 0 ? (
              <div style={{textAlign:"center",padding:"34px 20px",background:DS.card,borderRadius:DS.radiusCard,border:`0.5px solid ${DS.border}`}}>
                <div style={{width:52,height:52,borderRadius:15,background:DS.cielSoft,display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:10}}><BarChart3 size={24} strokeWidth={1.8} color={DS.marine}/></div>
                <div style={{fontSize:13,color:DS.textSecondary}}>Aucun dépôt enregistré pour {formatMois(month)}</div>
              </div>
            ) : (
              <>
                {/* Vue par logement */}
                <div style={{display:"inline-flex",alignItems:"center",gap:7,fontFamily:DS.fontDisplay,fontSize:15,fontWeight:600,color:DS.marineDark,marginBottom:8,marginTop:6}}><Home size={16} strokeWidth={2}/> Par logement</div>
                {logIdsSorted.map(logId => {
                  const sousLoc = isLogSousLoc(logId)
                  const entry = parLog[logId]
                  return (
                    <div key={logId} className="fbz-card" style={{background:DS.card,borderRadius:DS.radiusCard,padding:14,marginBottom:8,border:`0.5px solid ${DS.border}`,borderLeft:`3px solid ${sousLoc ? DS.ciel : DS.marine}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6,gap:8,flexWrap:"wrap"}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <span style={{display:"inline-flex",alignItems:"center",gap:5,fontWeight:700,fontSize:13,color:DS.marineDark}}><Home size={13} strokeWidth={2} color={DS.marine}/> {logName(logId)}</span>
                          {sousLoc && <span style={{display:"inline-flex",alignItems:"center",gap:4,background:DS.cielSoft,color:DS.marine,fontSize:9.5,fontWeight:700,padding:"2px 7px",borderRadius:6,letterSpacing:.3}}><Home size={10} strokeWidth={2.4}/> SOUS-LOC</span>}
                        </div>
                        <div style={{fontSize:14,fontWeight:700,fontFamily:DS.fontDisplay,color:DS.charge}}>{fmtE(entry.coutTotal)}</div>
                      </div>
                      <div style={{fontSize:10.5,color:DS.textMuted,marginBottom:6}}>{entry.depots.length} dépôt{entry.depots.length>1?"s":""}</div>
                      {entry.depots.map(d => (
                        <div key={d.id} style={{paddingLeft:8,borderLeft:`2px solid ${DS.border}`,marginBottom:4,fontSize:11.5}}>
                          <div style={{color:DS.textSecondary}}>
                            <span style={{fontWeight:700}}>{fmtJourMois(d.date)}</span> · {d.lignes.map(l => `${l.nbPrelevables} ${csName(l.csId)}`).join(", ")}
                            <span style={{marginLeft:6,color:DS.charge,fontWeight:700}}>{fmtE(d.coutTotal)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                })}

                {/* Vue par produit */}
                <div style={{display:"inline-flex",alignItems:"center",gap:7,fontFamily:DS.fontDisplay,fontSize:15,fontWeight:600,color:DS.marineDark,marginBottom:8,marginTop:18}}><Boxes size={16} strokeWidth={2}/> Par produit</div>
                {csIdsSorted.map(csId => {
                  const prod = allProduits.find(p => p.id === csId)
                  const entry = parProduit[csId]
                  const lab = prod ? labelPrelevable(prod) : "u"
                  return (
                    <div key={csId} className="fbz-card" style={{background:DS.card,borderRadius:DS.radiusCard,padding:"10px 14px",marginBottom:6,border:`0.5px solid ${DS.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:600,color:DS.marineDark}}>{csName(csId)}</div>
                        <div style={{fontSize:10.5,color:DS.textMuted,marginTop:2}}>{entry.totalPrelevable} {lab}{entry.totalPrelevable>1?"s":""} déposé{entry.totalPrelevable>1?"s":""} sur {entry.nbDepots} dépôt{entry.nbDepots>1?"s":""}</div>
                      </div>
                      <div style={{fontSize:13,fontWeight:700,fontFamily:DS.fontDisplay,color:DS.charge}}>{fmtE(entry.coutTotal)}</div>
                    </div>
                  )
                })}
              </>
            )}
          </>
        )
      })()}
      </>
      )}
        </>
      )}
    </div>
  )
}
